import React, { useState, useEffect, useMemo, useDeferredValue } from "react"
import styles from "../../asset/styles/agents/agents.module.scss"
import Table, { TableColumn } from "../../component/table/table"
import utilStyles from "../../asset/styles/util/util.module.scss"
import IconsColor from "util/iconsColor"
import { agent } from "apiClient/api"
import { AgencyData, AgentData, OfficeData } from "../../models"
import CustomizedMenu from "component/muiMenu/muiMenu"
import { useHotelGeneric } from "../../context/HotelGenericContext"
import { useSnackbar } from "../../context/snackbarContext"
import AddAgencyWizardPopup from "./AddAgencyWizardPopup"
import AgencyViewPopup from "./AgencyViewPopup"
import OfficesListPopup from "./OfficesListPopup"
import Popup from "../../component/popup/popup"
import MainTabsTableHeader, { FilterGroup } from "../../component/mainTabsTableHeader/mainTabsTableHeader"
import headerStyles from "../../component/mainTabsTableHeader/mainTabsTableHeader.module.scss"
import { exportAgencyAgentRowsToCsv } from "../../util/export/agencyExport"
import { buildAgencyAgentExportRows } from "../../util/export/agencyExportRows"
import { applyFilters, applySearch, cycleSort, sortRowsBy, uniqueOptionsFromRows } from "../../util/listFilters"
import { useListView, LIST_VIEW_KEYS } from "../../context/listFilterContext"
import { toTitleCase } from "util/textcase"
import { TablePaginationBar, useClientListPagination } from "../../component/tablePagination"
import AgentViewPopup from "./agentViewPopup"
import ImportAgencyPopup from "component/agencyImport/importAgencyPopup"
import { useUserDetail } from "context/userDetailContext"
import { isAdminUser } from "util/isAdminUser"
import { formatPhoneForDisplay } from "util/phoneDisplayUtil"

const AgenciesList: React.FC = () => {
    const { showError, showSuccess } = useSnackbar()
    const [agencies, setAgencies] = useState<AgencyData[]>([])
    const {
        search: searchValue, setSearch: setSearchValue,
        sortState, setSortState,
        filters: tableSelectedFilters, setFilters: setTableSelectedFilters,
        page, setPage, pageSize, setPageSize,
    } = useListView(LIST_VIEW_KEYS.agencies)
    const handleColumnSort = React.useCallback(
        (key: string) => setSortState((prev) => cycleSort(prev, key)),
        [setSortState],
    )
    const [addAgencyWizardOpen, setAddAgencyWizardOpen] = useState(false)
    const [editAgencyWizardOpen, setEditAgencyWizardOpen] = useState(false)
    const [agencyForEdit, setAgencyForEdit] = useState<AgencyData | null>(null)
    const [addAgentWizardOpen, setAddAgentWizardOpen] = useState(false)
    const [editAgentWizardOpen, setEditAgentWizardOpen] = useState(false)
    const [agentForEdit, setAgentForEdit] = useState<AgentData | null>(null)
    const [agentStatusPopupOpen, setAgentStatusPopupOpen] = useState(false)
    const [agentForStatus, setAgentForStatus] = useState<AgentData | null>(null)
    const [agentDeletePopupOpen, setAgentDeletePopupOpen] = useState(false)
    const [agentForDelete, setAgentForDelete] = useState<AgentData | null>(null)
    const [viewPopupOpen, setViewPopupOpen] = useState(false)
    const [agencyForView, setAgencyForView] = useState<AgencyData | null>(null)
    const [statusPopupOpen, setStatusPopupOpen] = useState(false)
    const [agencyForStatus, setAgencyForStatus] = useState<AgencyData | null>(null)
    const [deletePopupOpen, setDeletePopupOpen] = useState(false)
    const [agencyForDelete, setAgencyForDelete] = useState<AgencyData | null>(null)
    const [officesListPopupOpen, setOfficesListPopupOpen] = useState(false)
    const [agencyForOffices, setAgencyForOffices] = useState<AgencyData | null>(null)
    const [preloadedOffices, setPreloadedOffices] = useState<OfficeData[] | undefined>(undefined)
    const [preloadedAgents, setPreloadedAgents] = useState<any[] | undefined>(undefined);
    const [flagAgentPopupOpen, setFlagAgentPopupOpen] = useState(false);
    const { setHotelGenericData } = useHotelGeneric()
    const { userDetail } = useUserDetail()
    const canImport = isAdminUser(userDetail)
    const [importPopupOpen, setImportPopupOpen] = useState(false)

    const GetAgencyList = async () => {
        try {
            const response = await agent.AgencyListAPI.get()
            if (response?.status === true) {
                setAgencies(response?.data ?? [])
            } else {
                showError(response?.error?.message || "Failed to load agencies")
            }
        } catch (error) {
            console.log("GetAgencyList Error", error)
            showError("Failed to load agencies")
        }
    }

    const GetGenericHotelDetails = async () => {
        try {
            const response = await agent.GenericGlobalAPI.get()
            if (response?.status === false) {
                console.log("Failed to fetch generic hotel details", response?.error)
            } else {
                setHotelGenericData(response?.data[0])
            }
        } catch (error) {
            console.log("GetGenericHotelDetails Error", error)
        }
    }

    useEffect(() => {
        ;(async () => {
            await Promise.all([GetAgencyList(), GetGenericHotelDetails()])
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleOfficesClick = async (row: AgencyData) => {
        setAgencyForOffices(row)
        try {
            const response = await agent.OfficeListAPI.get(row.agency_id)
            setPreloadedOffices(response?.status === true ? (response?.data ?? []) : undefined)
        } catch {
            setPreloadedOffices(undefined)
        }
        setOfficesListPopupOpen(true)
    }

    const handleAgentsRowClick = async (row: AgencyData) => {
        setAgencyForOffices(row)
        try {
            const response = await agent.AgentListAPI.get(row.agency_id)
            setPreloadedAgents(response?.status === true ? (response?.data ?? []) : undefined)
        } catch {
            setPreloadedAgents(undefined)
        }
        setFlagAgentPopupOpen(true)
    }

    const refreshAgencyAgents = async () => {
        if (!agencyForOffices?.agency_id) return
        try {
            const response = await agent.AgentListAPI.get(agencyForOffices.agency_id)
            if (response?.status === true) {
                setPreloadedAgents(response?.data ?? [])
            }
        } catch (err) {
            console.error("Refresh agency agents error", err)
        }
    }

    const handleConfirmAgentStatusChange = async () => {
        const agentToUpdate = agentForStatus
        setAgentStatusPopupOpen(false)
        setAgentForStatus(null)
        if (!agentToUpdate?.agent_id) return
        try {
            const response = await agent.AgentDeleteAPI.put({
                agent_id: agentToUpdate.agent_id,
                flag_active: !agentToUpdate.flag_active,
                agent_flag_deleted: false,
            })
            if (response?.status === true) {
                showSuccess("Agent status updated successfully")
                await refreshAgencyAgents()
            } else {
                showError(response?.error?.message ?? "Failed to update agent status")
            }
        } catch (err) {
            console.error("Update agent status error", err)
            showError("Failed to update agent status")
        }
    }

    const handleConfirmAgentDelete = async () => {
        const agentToDelete = agentForDelete
        setAgentDeletePopupOpen(false)
        setAgentForDelete(null)
        if (!agentToDelete?.agent_id) return
        try {
            const response = await agent.AgentDeleteAPI.put({
                agent_id: agentToDelete.agent_id,
                flag_active: false,
                agent_flag_deleted: true,
            })
            if (response?.status === true) {
                showSuccess("Agent deleted successfully")
                await refreshAgencyAgents()
            } else {
                showError(response?.error?.message ?? "Failed to delete agent")
            }
        } catch (err) {
            console.error("Delete agent error", err)
            showError("Failed to delete agent")
        }
    }

    const columns: TableColumn<AgencyData>[] = [
        {
            key: "agency_name",
            label: "Agency Name",
            width: "20%",
            align: "left",
            render: (value: string, row: AgencyData) => (
                <span
                    style={{ color: "#1565C0", cursor: "pointer", fontWeight: 500 }}
                    onClick={() => {
                        setAgencyForView(row)
                        setViewPopupOpen(true)
                    }}
                >
                    {toTitleCase(value)}
                </span>
            ),
        },
        {
            key: "office",
            label: "No. of Offices",
            width: "12%",
            align: "left",
            render: (value: OfficeData[], row: AgencyData) => (
                <span
                    style={{ color: "#1565C0", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => handleOfficesClick(row)}
                >
                    {value?.length ?? 0}
                </span>
            ),
        },
        {
            key: "number_of_agent",
            label: "No. of Agents",
            width: "12%",
            align: "left",
            render: (value: number, row: AgencyData) => {
                console.log("value", value, row)
                return (<span
                    style={{ color: "#1565C0", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => handleAgentsRowClick(row)}
                >
                    {value}
                </span>
            )},
        },
        {
            key: "contact_person_name",
            label: "Primary Contact Info",
            width: "20%",
            align: "left",
            render: (value: string, row: AgencyData) => (
                <div>
                    <div>{toTitleCase(value)}</div>
                    <div style={{ fontSize: "12px", color: "#7a7a7a" }}>{formatPhoneForDisplay(row.phone_number)}</div>
                    <div style={{ fontSize: "12px", color: "#7a7a7a" }}>{row.email}</div>
                </div>
            ),
        },
        {
            key: "flag_active",
            label: "Status",
            width: "10%",
            align: "left",
            render: (value: boolean) => (
                <span
                    className={
                        value === true
                            ? utilStyles.statusBadgeActive
                            : value === false
                              ? utilStyles.statusBadgeInactive
                              : utilStyles.statusBadgeInactive
                    }
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            key: "actions",
            label: "",
            width: "5%",
            align: "left",
            render: (_: unknown, row: AgencyData) => (
                <CustomizedMenu
                    buttonText=""
                    menuItems={[
                        {
                            label: `Set Status to ${row.flag_active ? "Inactive" : "Active"}`,
                            onClick: () => {
                                setAgencyForStatus(row)
                                setStatusPopupOpen(true)
                            },
                        },
                        {
                            label: "Delete",
                            onClick: () => {
                                setAgencyForDelete(row)
                                setDeletePopupOpen(true)
                            },
                            style: { color: "#d32f2f" },
                        },
                    ]}
                    buttonVariant="text"
                    buttonStyle={{ padding: "0px", minWidth: "0px" }}
                    buttonEndIcon={
                        <IconsColor iconName="moreVert" width="20" height="20" fillColor="none" strokeColor="#7a7a7a" />
                    }
                />
            ),
        },
    ]

    // ----- Filter accessors (Status, Editor per spec) -----
    const FILTER_ACCESSORS = useMemo(
        () => ({
            status: (row: AgencyData) => (row.flag_active ? "Active" : "Inactive"),
            editor: (row: AgencyData) => String((row as any).updated_by_user_name || ""),
        }),
        [],
    )

    const filterGroups: FilterGroup[] = useMemo(
        () => [
            {
                key: "status",
                label: "Status",
                options: [
                    { label: "Active", value: "Active" },
                    { label: "Inactive", value: "Inactive" },
                ],
            },
            { key: "editor", label: "Editor", options: uniqueOptionsFromRows(agencies, FILTER_ACCESSORS.editor) },
        ],
        [agencies, FILTER_ACCESSORS],
    )

    // Defer heavy table re-render so clearing search/filters doesn't block the UI.
    const deferredSearch = useDeferredValue(searchValue)
    const deferredFilters = useDeferredValue(tableSelectedFilters)
    const filteredAgencies = useMemo(() => {
        let rows = applyFilters(agencies, deferredFilters, FILTER_ACCESSORS)
        rows = applySearch(rows, deferredSearch, [(r) => r.agency_name, (r) => r.contact_person_name])
        rows = sortRowsBy(rows, sortState)
        return rows
    }, [agencies, deferredFilters, deferredSearch, sortState, FILTER_ACCESSORS])

    const paginationQueryKey = JSON.stringify({ search: searchValue, filters: tableSelectedFilters, sortState })
    const {
        pagedRows: pagedAgencies,
        total: pagedTotal,
        page: currentPage,
        pageSize: currentPageSize,
        handlePageChange,
        handlePageSizeChange,
    } = useClientListPagination(filteredAgencies, {
        page, pageSize, setPage, setPageSize, queryKey: paginationQueryKey,
    })

    const handleConfirmStatusChange = async () => {
        const agencyToUpdate = agencyForStatus
        setStatusPopupOpen(false)
        setAgencyForStatus(null)
        if (!agencyToUpdate?.agency_id) return
        try {
            const response = await agent.AgencyDeleteAPI.put({
                agency_id: agencyToUpdate.agency_id,
                flag_active: !agencyToUpdate.flag_active,
                agency_flag_deleted: false,
            })
            if (response?.status === true) {
                showSuccess("Agency status updated successfully")
                await GetAgencyList()
            } else {
                showError(response?.error?.message ?? "Failed to update agency status")
            }
        } catch (err) {
            console.error("Update agency status error", err)
            showError("Failed to update agency status")
        }
    }

    const handleExportCsv = async () => {
        if (pagedAgencies.length === 0) {
            showError("No agencies to export")
            return
        }

        try {
            const response = await agent.AgentListAPI.get("", true)
            if (response?.status !== true) {
                showError(response?.error?.message || "Failed to load agents for export")
                return
            }

            const exportRows = buildAgencyAgentExportRows(pagedAgencies, response?.data ?? [])
            if (exportRows.length === 0) {
                showError("No agent data to export for the agencies on this page")
                return
            }

            exportAgencyAgentRowsToCsv(exportRows)
            showSuccess(`Exported ${exportRows.length} rows`)
        } catch (error) {
            console.error("Export agencies CSV error", error)
            showError("Failed to export agencies")
        }
    }

    const handleConfirmDelete = async () => {
        const agencyToDelete = agencyForDelete
        setDeletePopupOpen(false)
        setAgencyForDelete(null)
        if (!agencyToDelete?.agency_id) return
        try {
            const response = await agent.AgencyDeleteAPI.put({
                agency_id: agencyToDelete.agency_id,
                flag_active: false,
                agency_flag_deleted: true,
            })
            if (response?.status === true) {
                showSuccess("Agency deleted successfully")
                await GetAgencyList()
            } else {
                showError(response?.error?.message ?? "Failed to delete agency")
            }
        } catch (err) {
            console.error("Delete agency error", err)
            showError("Failed to delete agency")
        }
    }

    return (
        <>
            <div className={utilStyles.listPageFill}>
            <div className={utilStyles.listPageHeader}>
                    <MainTabsTableHeader
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        searchPlaceholder="Search agencies"
                        totalCount={agencies.length}
                        filteredCount={filteredAgencies.length}
                        itemLabel="agencies"
                        sortValue=""
                        onSortChange={() => undefined}
                        sortOptions={[]}
                        filterGroups={filterGroups}
                        selectedFilters={tableSelectedFilters}
                        onFiltersChange={setTableSelectedFilters}
                        addButtonLabel="Agency"
                        onAddClick={() => {
                            setAddAgencyWizardOpen(true)
                        }}
                        showResultCount={false}
                        extraActions={
                            <>
                                {canImport && (
                                    <button
                                        type="button"
                                        className={`${headerStyles.filtersTrigger} ${headerStyles.primaryOutlineTrigger}`}
                                        onClick={() => setImportPopupOpen(true)}
                                    >
                                        <span>Import</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`${headerStyles.filtersTrigger} ${headerStyles.primaryOutlineTrigger}`}
                                    onClick={handleExportCsv}
                                >
                                    <span>Export</span>
                                </button>
                            </>
                        }
                    />
            </div>
            <div className={utilStyles.listPageTableBlock}>
                <div className={utilStyles.listPageTableScroll}>
                    <Table
                        columns={columns}
                        data={pagedAgencies}
                        keyExtractor={(row: AgencyData) => row.agency_id}
                        emptyMessage="No agencies available"
                        minWidth={1000}
                        showHeader={true}
                        fillHeight={true}
                        sortKey={sortState.key}
                        sortDirection={sortState.direction}
                        onSort={handleColumnSort}
                    />
                </div>
                <TablePaginationBar
                    total={pagedTotal}
                    page={currentPage}
                    pageSize={currentPageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>
            </div>

            <AgencyViewPopup
                isOpen={viewPopupOpen}
                onClose={() => {
                    setViewPopupOpen(false)
                    setAgencyForView(null)
                }}
                agency={agencyForView}
                onEdit={() => {
                    setViewPopupOpen(false)
                    setAgencyForEdit(agencyForView)
                    setEditAgencyWizardOpen(true)
                }}
            />

            <ImportAgencyPopup
                isOpen={importPopupOpen}
                onClose={() => setImportPopupOpen(false)}
                onSuccess={GetAgencyList}
            />

            <AddAgencyWizardPopup
                isOpen={addAgencyWizardOpen}
                onClose={() => setAddAgencyWizardOpen(false)}
                onSuccess={GetAgencyList}
            />

            <AddAgencyWizardPopup
                isOpen={editAgencyWizardOpen}
                onClose={() => {
                    setEditAgencyWizardOpen(false)
                    setAgencyForEdit(null)
                }}
                onSuccess={GetAgencyList}
                wizardMode="editAgency"
                agency={agencyForEdit}
                initialStep={1}
            />

            <AddAgencyWizardPopup
                isOpen={addAgentWizardOpen}
                onClose={() => setAddAgentWizardOpen(false)}
                onSuccess={() => {
                    void refreshAgencyAgents()
                    GetAgencyList()
                }}
                wizardMode="addAgents"
                agency={agencyForOffices}
                initialStep={3}
            />

            <AddAgencyWizardPopup
                isOpen={editAgentWizardOpen}
                onClose={() => {
                    setEditAgentWizardOpen(false)
                    setAgentForEdit(null)
                }}
                onSuccess={() => {
                    void refreshAgencyAgents()
                    GetAgencyList()
                    setFlagAgentPopupOpen(true)
                }}
                wizardMode="editAgent"
                agency={agencyForOffices}
                editingAgent={agentForEdit}
                initialStep={3}
            />

            <AgentViewPopup
                isOpen={flagAgentPopupOpen}
                onClose={() => {
                    setFlagAgentPopupOpen(false)
                    setPreloadedAgents(undefined)
                }}
                mode="add"
                agentsData={preloadedAgents ?? []}
                agencyName={agencyForOffices?.agency_name}
                onAddAgentClick={() => {
                    setFlagAgentPopupOpen(false)
                    setAddAgentWizardOpen(true)
                }}
                onEditAgent={(row) => {
                    setAgentForEdit(row)
                    setFlagAgentPopupOpen(false)
                    setEditAgentWizardOpen(true)
                }}
                onStatusChange={(row) => {
                    setAgentForStatus(row)
                    setAgentStatusPopupOpen(true)
                }}
                onDeleteAgent={(row) => {
                    setAgentForDelete(row)
                    setAgentDeletePopupOpen(true)
                }}
            />

            {/* <AgentPopup
                isOpen={flagAgentPopupOpen}
                onClose={() => {
                    setFlagAgentPopupOpen(false)
                    setSelectedAgent(null)
                }}
                mode={agentPopupMode}
                agent={selectedAgent}
                agencies={agencies}
                onSuccess={GetAgencyList}
            /> */}

            <OfficesListPopup
                isOpen={officesListPopupOpen}
                onClose={() => {
                    setOfficesListPopupOpen(false)
                    setAgencyForOffices(null)
                    setPreloadedOffices(undefined)
                }}
                agency={agencyForOffices}
                onSuccess={GetAgencyList}
                initialOffices={preloadedOffices}
            />

            <Popup
                isOpen={agentStatusPopupOpen}
                onClose={() => {
                    setAgentStatusPopupOpen(false)
                    setAgentForStatus(null)
                }}
                title="Change Agent Status"
                primaryButton={{ label: "Confirm", onClick: handleConfirmAgentStatusChange }}
                secondaryButton={{
                    label: "Cancel",
                    onClick: () => {
                        setAgentStatusPopupOpen(false)
                        setAgentForStatus(null)
                    },
                }}
                heightFiexd={true}
            >
                {agentForStatus && (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="warningSign"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#FF0000"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to change the status of <strong>{agentForStatus.agent_name}</strong>{" "}
                            to <strong>{agentForStatus.flag_active ? "Inactive" : "Active"}</strong>?
                        </p>
                    </div>
                )}
            </Popup>

            <Popup
                isOpen={agentDeletePopupOpen}
                onClose={() => {
                    setAgentDeletePopupOpen(false)
                    setAgentForDelete(null)
                }}
                title="Delete Agent"
                primaryButton={{ label: "Delete", onClick: handleConfirmAgentDelete }}
                secondaryButton={{
                    label: "Cancel",
                    onClick: () => {
                        setAgentDeletePopupOpen(false)
                        setAgentForDelete(null)
                    },
                }}
                heightFiexd={true}
            >
                {agentForDelete && (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="deleteIcon"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#d32f2f"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to delete <strong>{agentForDelete.agent_name}</strong>?
                        </p>
                    </div>
                )}
            </Popup>

            <Popup
                isOpen={statusPopupOpen}
                onClose={() => {
                    setStatusPopupOpen(false)
                    setAgencyForStatus(null)
                }}
                title="Change Agency Status"
                primaryButton={{ label: "Confirm", onClick: handleConfirmStatusChange }}
                secondaryButton={{
                    label: "Cancel",
                    onClick: () => {
                        setStatusPopupOpen(false)
                        setAgencyForStatus(null)
                    },
                }}
                heightFiexd={true}
            >
                {agencyForStatus && (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="warningSign"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#FF0000"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to change the status of <strong>{agencyForStatus.agency_name}</strong>{" "}
                            to <strong>{agencyForStatus.flag_active ? "Inactive" : "Active"}</strong>?
                        </p>
                    </div>
                )}
            </Popup>

            <Popup
                isOpen={deletePopupOpen}
                onClose={() => {
                    setDeletePopupOpen(false)
                    setAgencyForDelete(null)
                }}
                title="Delete Agency"
                primaryButton={{ label: "Delete", onClick: handleConfirmDelete }}
                secondaryButton={{
                    label: "Cancel",
                    onClick: () => {
                        setDeletePopupOpen(false)
                        setAgencyForDelete(null)
                    },
                }}
                heightFiexd={true}
            >
                {agencyForDelete && (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="deleteIcon"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#d32f2f"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to permanently delete <strong>{agencyForDelete.agency_name}</strong>?
                            This action cannot be undone.
                        </p>
                    </div>
                )}
            </Popup>
        </>
    )
}

export default AgenciesList
